import React, { useState } from 'react';
import { Comment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import FormattedDate from '../FormattedDate';
import Button from './Button';
import { AiOutlineEdit, AiOutlineDelete, AiOutlineCheck, AiOutlineClose, AiOutlineMessage } from 'react-icons/ai';
import CommentForm from './CommentForm';

interface CommentItemProps {
  comment: Comment;
  onReply: (commentId: string) => void;
  onEdit: (commentId: string, content: string) => void;
  onDelete: (commentId: string) => void;
  onApprove?: (commentId: string) => void;
  onReject?: (commentId: string, reason: string) => void;
  isReplying?: boolean;
  replyingToId?: string | null;
  onCancelReply?: () => void;
  onSubmitReply?: (content: string) => Promise<void>;
  onFetchMoreReplies?: (commentId: string) => Promise<void>;
  depth?: number;
  className?: string;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onReply,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  isReplying = false,
  replyingToId = null,
  onCancelReply,
  onSubmitReply,
  onFetchMoreReplies,
  depth = 0,
  className = ''
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDeepReplies, setShowDeepReplies] = useState(false);
  const [isLoadingMoreReplies, setIsLoadingMoreReplies] = useState(false);

  const isAuthor = user?.id === comment.author_id;
  const isAdmin = user?.user_role === 'admin';
  const canModerate = isAdmin || isAuthor;
  const maxDepth = 5; // Prevent infinite nesting
  const isDeepNesting = depth >= maxDepth;

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onEdit(comment.id, editContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to edit comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || !onReject) return;
    
    setIsSubmitting(true);
    try {
      await onReject(comment.id, rejectReason);
      setShowRejectForm(false);
      setRejectReason('');
    } catch (error) {
      console.error('Failed to reject comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'rejected': return 'text-red-600';
      case 'deleted': return 'text-gray-500';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'pending': return 'Pending';
      case 'rejected': return 'Rejected';
      case 'deleted': return 'Deleted';
      default: return status;
    }
  };

  return (
    <div className={`${className} ${depth > 0 ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''}`}>
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
        {/* Comment Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-[#4B3F72]">
              {comment.author.first_name} {comment.author.last_name}
            </span>
            <span className="text-sm text-gray-500">
              <FormattedDate date={comment.created_at} />
            </span>
            {comment.status !== 'approved' && (
              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(comment.status)} bg-opacity-10`}>
                {getStatusText(comment.status)}
              </span>
            )}
          </div>
          
          {/* Action Buttons */}
          {canModerate && comment.status !== 'deleted' && (
            <div className="flex items-center space-x-2">
              {comment.status === 'pending' && onApprove && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => onApprove(comment.id)}
                  className="px-2 py-1 text-xs"
                >
                  <AiOutlineCheck className="w-3 h-3 mr-1" />
                  Approve
                </Button>
              )}
              
              {comment.status === 'pending' && onReject && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowRejectForm(true)}
                  className="px-2 py-1 text-xs"
                >
                  <AiOutlineClose className="w-3 h-3 mr-1" />
                  Reject
                </Button>
              )}
              
              {isAuthor && comment.status === 'approved' && (
                <>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setIsEditing(true)}
                    className="px-2 py-1 text-xs"
                  >
                    <AiOutlineEdit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => onDelete(comment.id)}
                    className="px-2 py-1 text-xs"
                  >
                    <AiOutlineDelete className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Comment Content */}
        {isEditing ? (
          <div className="mb-3">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-[#4B3F72] focus:border-[#4B3F72] text-gray-700"
              rows={3}
            />
                         <div className="flex space-x-2 mt-2">
               <Button
                 variant="primary"
                 size="small"
                 onClick={handleEdit}
                 disabled={isSubmitting}
                 className="px-3 py-1"
               >
                 {isSubmitting ? 'Saving...' : 'Save'}
               </Button>
               <Button
                 variant="secondary"
                 size="small"
                 onClick={() => {
                   setIsEditing(false);
                   setEditContent(comment.content);
                 }}
                 className="px-3 py-1"
               >
                 Cancel
               </Button>
             </div>
          </div>
        ) : (
          <div className="text-gray-800 mb-3">
            {comment.status === 'deleted' ? (
              <em className="text-gray-500">[Comment deleted]</em>
            ) : (
              <div className="whitespace-pre-wrap">{comment.content}</div>
            )}
          </div>
        )}

        {/* Reject Form */}
        {showRejectForm && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-2 bg-gray-100 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500 text-gray-700 placeholder-gray-400"
              rows={2}
            />
                         <div className="flex space-x-2 mt-2">
               <Button
                 variant="danger"
                 size="small"
                 onClick={handleReject}
                 disabled={isSubmitting}
                 className="px-3 py-1"
               >
                 {isSubmitting ? 'Rejecting...' : 'Reject'}
               </Button>
               <Button
                 variant="secondary"
                 size="small"
                 onClick={() => {
                   setShowRejectForm(false);
                   setRejectReason('');
                 }}
                 className="px-3 py-1"
               >
                 Cancel
               </Button>
             </div>
          </div>
        )}

        {/* Reply Button */}
        {comment.status === 'approved' && (
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="small"
              onClick={() => onReply(comment.id)}
              className="px-2 py-1 text-xs"
            >
              <AiOutlineMessage className="w-3 h-3 mr-1" />
              Reply
            </Button>
            {comment.reply_count > 0 && (
              <span className="text-sm text-gray-500">
                {comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}
              </span>
            )}
          </div>
        )}

        {/* Inline Reply Form */}
        {isReplying && onSubmitReply && onCancelReply && (
          <div className="mt-3 ml-6 border-l-2 border-gray-200 pl-4">
            <CommentForm
              onSubmit={onSubmitReply}
              onCancel={onCancelReply}
              placeholder="Write a reply..."
              submitLabel="Post Reply"
            />
          </div>
        )}

        {/* Moderation Reason */}
        {comment.moderation_reason && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
            <span className="text-sm text-red-700">
              <strong>Rejection reason:</strong> {comment.moderation_reason}
            </span>
          </div>
        )}
      </div>

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {/* Show deep nested replies only if explicitly requested */}
          {isDeepNesting ? (
            showDeepReplies ? (
              // Show all deep nested replies
              <div>
                {comment.replies.map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    onReply={onReply}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onApprove={onApprove}
                    onReject={onReject}
                    isReplying={replyingToId === reply.id}
                    replyingToId={replyingToId}
                    onCancelReply={onCancelReply}
                    onSubmitReply={onSubmitReply}
                    onFetchMoreReplies={onFetchMoreReplies}
                    depth={depth + 1}
                  />
                ))}
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setShowDeepReplies(false)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Hide replies
                  </Button>
                </div>
              </div>
            ) : (
              // Show "Show more replies" button for deep nesting
              <div className="mt-2">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowDeepReplies(true)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Show {comment.reply_count} more {comment.reply_count === 1 ? 'reply' : 'replies'}
                </Button>
              </div>
            )
          ) : (
            // Show normal nested replies (depth < maxDepth)
            <div>
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onApprove={onApprove}
                  onReject={onReject}
                  isReplying={replyingToId === reply.id}
                  replyingToId={replyingToId}
                  onCancelReply={onCancelReply}
                  onSubmitReply={onSubmitReply}
                  onFetchMoreReplies={onFetchMoreReplies}
                  depth={depth + 1}
                />
              ))}
              {/* Show "Load more replies" button if there are more replies available */}
              {comment.hasMoreReplies && onFetchMoreReplies && (
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={async () => {
                      setIsLoadingMoreReplies(true);
                      try {
                        await onFetchMoreReplies(comment.id);
                      } catch (error) {
                        console.error('Failed to load more replies:', error);
                      } finally {
                        setIsLoadingMoreReplies(false);
                      }
                    }}
                    disabled={isLoadingMoreReplies}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    {isLoadingMoreReplies ? 'Loading...' : 'Load more replies'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
